import {
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  IconButton,
  Modal,
  Box,
} from "@mui/material";
import "./EditBrand.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import { BrandApi } from "../../../../utils/api/Brand";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import Upload from "../../../../components/icons/Upload";
import SearchIcon from "../../../../components/icons/SearchIcon";
import TrueIcon from "../../../../components/icons/TrueIcon";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BrandProfileLogo from "./BrandProfileLogo";
import ThemePreferences from "./ThemePreferences";
import ThemeErrorSummary from "./ThemeErrorSummary";
import Loading from "../../../loading/Loading";
import ActionMenuIcon from "./ActionMenuIcon";
import { CommonEnums } from "../../../../enums/common.enums";
import { LocalStorageHelpers } from "../../../../utils/helpers/localstorage.helpers";
import { UserApi } from "../../../../utils/api/User";
import FontSelectorModal, { FontItem } from "../FontSelector/FontSelectorModal";

const EditBrand = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [isFontUploading, setIsFontUploading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isFontProcessing, setIsFontProcessing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [systemFonts, setSystemFonts] = useState<any[]>([]);
  const [customFonts, setCustomFonts] = useState<any[]>([]);
  const customFontSectionLabel = "Brand fonts";

  const [isSaving, setIsSaving] = useState(false);

  const [openTitleFontModal, setOpenTitleFontModal] = useState(false);
  const [openSubtitleFontModal, setOpenSubtitleFontModal] = useState(false);
  const [openBodyFontModal, setOpenBodyFontModal] = useState(false);

  const [brandId, setBrandId] = useState<string | null>(null);
  const [brandMeta, setBrandMeta] = useState({
    status: "Active",
    createdAt: "",
    updatedAt: "",
  });
  const [associatedAgencyId, setAssociatedAgencyId] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  const [canEdit, setCanEdit] = useState(false);

  const [contrastWarnings, setContrastWarnings] = useState<
    Record<string, string>
  >({});

  const [originalColors, setOriginalColors] = useState({
    base_color_primary: null,
    base_color_secondary: null,
    text_color_primary: null,
    text_color_secondary: null,
  });

  const navigate = useNavigate();
  const { id: urlBrandId } = useParams<{ id: string }>();

  useEffect(() => {
    const checkPermissions = async () => {
      const localData: any = LocalStorageHelpers.getUserData();

      if (localData?._id) {
        try {
          const res = await UserApi.getUserByid(localData._id);

          if (res?.data?.success) {
            const userData = res.data.data;
            const SUPER_ADMIN_SLUG = "super-admin";
            const SUPER_ADMIN_ID = "67aca623ef6170851a306d55";

            if (
              typeof userData.user_role === "object" &&
              userData.user_role?.slug === SUPER_ADMIN_SLUG
            ) {
              setCanEdit(true);
            } else if (
              typeof userData.user_role === "string" &&
              userData.user_role === SUPER_ADMIN_ID
            ) {
              setCanEdit(true);
            } else {
              setCanEdit(false);
            }
          }
        } catch (error) {
          console.error("Error checking permissions", error);
          setCanEdit(false);
        }
      }
    };

    checkPermissions();
  }, []);

  const hexToRgb = (hex: string) => {
    const cleaned = hex.replace("#", "");
    const bigint = parseInt(cleaned, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const getLuminance = (r: number, g: number, b: number) => {
    const toLinear = (c: number) => {
      c /= 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const R = toLinear(r);
    const G = toLinear(g);
    const B = toLinear(b);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  };

  const getContrastRatio = (bg: any, text: any) => {
    const bgColor = normalizeColor(bg);
    const textColor = normalizeColor(text);
    if (!bgColor || !textColor) return null;

    const L1 = getLuminance(bgColor.r, bgColor.g, bgColor.b);
    const L2 = getLuminance(textColor.r, textColor.g, textColor.b);
    const lighter = Math.max(L1, L2);
    const darker = Math.min(L1, L2);
    return (lighter + 0.05) / (darker + 0.05);
  };

  useEffect(() => {
    if (urlBrandId) {
      setBrandId(urlBrandId);
      fetchBrandDetails(urlBrandId);
    } else {
      toast.error("No Brand ID found");
      navigate("/brand-list");
    }
  }, [urlBrandId]);

  const fetchBrandDetails = async (id: string) => {
    setIsLoading(true);
    try {
      const [brandRes, settingsRes] = await Promise.all([
        BrandApi.getBrandById(id),
        BrandApi.getBrandSettings(id),
      ]);

      if (brandRes?.data?.success) {
        const brand = brandRes.data.data;
        const brandLogoRecord = brand.brand_logo;

        setOriginalColors({
          base_color_primary:
            brand.base_color_primary ||
            brand.theme_colors_response?.[0] ||
            null,
          base_color_secondary:
            brand.base_color_secondary ||
            brand.theme_colors_response?.[1] ||
            null,
          text_color_primary:
            brand.text_color_primary ||
            brand.theme_colors_response?.[2] ||
            null,
          text_color_secondary: brand.text_color_secondary || {
            r: 255,
            g: 255,
            b: 255,
            hex: "#FFFFFF",
          },
        });

        if (brand.agency_id) {
          setAssociatedAgencyId(
            typeof brand.agency_id === "object"
              ? brand.agency_id._id
              : brand.agency_id,
          );
        }

        const settings = settingsRes?.success ? settingsRes.data : null;

        let extractedColors =
          brand.theme_colors_response || brand.theme_colors || [];

        if (typeof extractedColors === "string") {
          try {
            extractedColors = JSON.parse(extractedColors);
          } catch (e) {
            extractedColors = [];
          }
        }
        formik.setValues({
          brand_name: brand.brand_name || "",
          brand_country: brand.brand_country || "",
          brand_logo:
            brandLogoRecord?._id || brandLogoRecord?.original_file_path || null,
          brandLogoUploadRecord: brandLogoRecord || null,
          theme_colors: (extractedColors || []).map(normalizeColor),
          theme_colors_response: (extractedColors || []).map(normalizeColor),
          base_color_primary: normalizeColor(brand.base_color_primary),
          base_color_secondary: normalizeColor(brand.base_color_secondary),
          text_color_primary: normalizeColor(brand.text_color_primary),
          text_color_secondary: normalizeColor(brand.text_color_secondary),
          brandFonts: [],
          font_configuration: settings?.font_configuration || "brand_fonts",
          title_fonts: settings?.title_fonts || "design_default",
          subtitle_fonts: settings?.subtitle_fonts || "design_default",
          body_text_fonts: settings?.body_text_fonts || "design_default",
          title_fonts_selected_from:
            settings?.title_fonts_selected_from || null,
          subtitle_fonts_selected_from:
            settings?.subtitle_fonts_selected_from || null,
          body_text_fonts_selected_from:
            settings?.body_text_fonts_selected_from || null,
        });

        setBrandMeta({
          status: brand.status || "",
          createdAt: formatDate(brand.createdAt),
          updatedAt: formatDate(brand.updatedAt),
        });

        await fetchCustomFonts(id);

        if (settings) {
          await fetchSavedSystemFonts([
            settings.title_fonts,
            settings.subtitle_fonts,
            settings.body_text_fonts,
          ]);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load brand details");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchCustomFonts = async (currentBrandId: string | null = brandId) => {
    if (!currentBrandId) return;
    try {
      const res = await BrandApi.GetFonts({
        fonts_for: "brand",
        brand_id: currentBrandId,
      });

      if (!res.success || !res.data) {
        setCustomFonts([]);
        return;
      }

      let fontsArray: any[] = [];
      if (Array.isArray(res.data)) {
        fontsArray = res.data;
      } else if (Array.isArray(res.data?.docs)) {
        fontsArray = res.data.docs;
      } else if (Array.isArray(res.data?.data?.docs)) {
        fontsArray = res.data.data.docs;
      } else if (Array.isArray(res.data?.data)) {
        fontsArray = res.data.data;
      }

      const variantFontsForInjection: any[] = [];
      fontsArray.forEach((font: any) => {
        variantFontsForInjection.push(font);
        if (Array.isArray(font.variants)) {
          font.variants.forEach((v: any) => {
            variantFontsForInjection.push({
              ...v,
              font_family: font.font_family,
            });
          });
        }
      });

      injectFontStyles(variantFontsForInjection, "custom-brand-fonts");

      const mapped = fontsArray.map((font: any) => ({
        label: font.font_family,
        value: font.default_variant_id || font._id,
        family: font.font_family,
        fontWeight: font.font_weight || "400",
        fontName: font.font_name,
      }));
      setCustomFonts(mapped);

      const uploadedList = fontsArray.map((font: any) => {
        let availableWeights: string[] = [];

        if (
          Array.isArray(font.available_weights) &&
          font.available_weights.length > 0
        ) {
          availableWeights = font.available_weights;
        } else if (Array.isArray(font.variants) && font.variants.length > 0) {
          availableWeights = font.variants.map((v: any) =>
            v.is_italic ? `${v.font_weight} Italic` : v.font_weight,
          );
        } else {
          availableWeights = [
            font.is_italic
              ? `${font.font_weight} Italic`
              : font.font_weight || "400",
          ];
        }

        return {
          id: font._id,
          family: font.font_family,
          availableWeights,
          totalVariants: font.total_variants || availableWeights.length,
        };
      });

      formik.setFieldValue("brandFonts", uploadedList);
    } catch (error) {
      console.error("Error fetching custom fonts:", error);
    }
  };

  const fetchSavedSystemFonts = async (fontIds: string[]) => {
    const uniqueIds = Array.from(
      new Set(fontIds.filter((id) => id && id !== "design_default")),
    );

    if (uniqueIds.length === 0) return;

    try {
      const res = await BrandApi.ListSystemFonts({ page: 1, limit: 1000 });

      if (!res?.success) return;

      const data = res.data?.data || res.data;
      const allFonts: any[] = data?.docs || [];

      const matchedFonts = allFonts.filter((font: any) => {
        const fontId = font.default_variant_id || font._id;
        return uniqueIds.includes(fontId);
      });

      if (matchedFonts.length === 0) return;

      const cdnBaseUrl = (process.env.REACT_APP_CDN_BASE_URL || "").replace(
        /\/$/,
        "",
      );

      let fontStyles = "";
      matchedFonts.forEach((font: any) => {
        const key = (font.woff2_font_storage_key || "").trim();
        if (!key) return;
        const storageKey = key.replace(/^\//, "");
        const fullUrl = `${cdnBaseUrl}/${storageKey}`;

        fontStyles += `
        @font-face {
          font-family: "${font.font_family}";
          src: url("${fullUrl}") format("woff2");
          font-weight: ${font.font_weight || "400"};
          font-style: ${font.is_italic ? "italic" : "normal"};
          font-display: swap;
        }
      `;
      });

      let styleTag = document.getElementById(
        "saved-system-fonts",
      ) as HTMLStyleElement;
      if (!styleTag) {
        styleTag = document.createElement("style");
        styleTag.id = "saved-system-fonts";
        document.head.appendChild(styleTag);
      }
      styleTag.innerHTML = fontStyles;

      const mapped: FontItem[] = matchedFonts.map((font: any) => ({
        label: font.font_family,
        value: font.default_variant_id || font._id,
        family: font.font_family,
      }));

      setSystemFonts((prev) => {
        const merged = [...prev];
        mapped.forEach((f) => {
          if (!merged.some((x) => x.value === f.value)) merged.push(f);
        });
        return merged;
      });
    } catch (error) {
      console.error("Error fetching saved system fonts:", error);
    }
  };

  // const fetchSystemFonts = async () => {
  //   try {
  //     const res = await BrandApi.ListSystemFonts({ page: 1, limit: 100 });

  //     if (res.success && res.data?.data?.docs) {
  //       const docs = res.data.data.docs;

  //       const valid = docs.filter(
  //         (f: any) =>
  //           f.woff2_font_storage_key && f.woff2_font_storage_key.trim() !== "",
  //       );

  //       injectFontStyles(valid, "system-fonts-style");

  //       const mapped = valid.map((font: any) => ({
  //         label: font.font_family,
  //         value: font.default_variant_id || font._id,
  //         family: font.font_family,
  //       }));

  //       setSystemFonts(mapped);
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  const injectFontStyles = (fontsArray: any[], styleId: string) => {
    const cdnBaseUrl = (process.env.REACT_APP_CDN_BASE_URL || "").replace(
      /\/$/,
      "",
    );
    let fontStyles = "";

    fontsArray.forEach((font: any) => {
      const key = (font.woff2_font_storage_key || "").trim();
      if (!key) return;

      const storageKey = key.replace(/^\//, "");
      const fullUrl = `${cdnBaseUrl}/${storageKey}`;

      fontStyles += `
      @font-face {
        font-family: "${font.font_family}";
        src: url("${fullUrl}") format("woff2");
        font-weight: ${font.font_weight || "400"};
        font-style: ${font.is_italic ? "italic" : "normal"};
        font-display: swap;
      }
    `;
    });

    let styleTag = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = fontStyles;
  };

  const handleFontFiles = async (files: FileList) => {
    if (!brandId) return;

    setIsFontUploading(true);
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("fonts", file));
    formData.append("brand_id", brandId);

    try {
      const res = await BrandApi.UploadFonts(formData, "brand");

      if (!res.success) {
        toast.error(res.error || "Failed to upload fonts");
        setIsFontUploading(false);
        setIsFontProcessing(false);
        return;
      }

      console.log("📤 Upload response:", res.data);

      setIsFontUploading(false);
      setIsFontProcessing(false);

      await fetchCustomFonts(brandId);

      toast.success("Fonts uploaded successfully!");

      (async () => {
        await new Promise((resolve) => setTimeout(resolve, 3000));

        let attempts = 0;
        const maxAttempts = 10;

        while (attempts < maxAttempts) {
          attempts++;
          console.log(
            `Background CDN check - attempt ${attempts}/${maxAttempts}`,
          );

          try {
            const checkRes = await BrandApi.GetFonts({
              fonts_for: "brand",
              brand_id: brandId,
            });

            if (checkRes.success && checkRes.data) {
              let arr: any[] = [];
              if (Array.isArray(checkRes.data)) arr = checkRes.data;
              else if (Array.isArray(checkRes.data?.docs))
                arr = checkRes.data.docs;
              else if (Array.isArray(checkRes.data?.data?.docs))
                arr = checkRes.data.data.docs;
              else if (Array.isArray(checkRes.data?.data))
                arr = checkRes.data.data;

              const allReady =
                arr.length > 0 &&
                arr.every((f: any) => {
                  if (
                    f.woff2_font_storage_key &&
                    f.woff2_font_storage_key.trim() !== ""
                  ) {
                    return true;
                  }
                  if (Array.isArray(f.variants) && f.variants.length > 0) {
                    return f.variants.some(
                      (v: any) =>
                        v.woff2_font_storage_key &&
                        v.woff2_font_storage_key.trim() !== "",
                    );
                  }
                  return false;
                });

              if (allReady) {
                console.log("All fonts ready on CDN!");
                await fetchCustomFonts(brandId);
                break;
              }
            }
          } catch (err) {
            console.error("Background polling error:", err);
          }

          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      })();
    } catch (error) {
      console.error(error);
      toast.error("Upload failed.");
      setIsFontUploading(false);
      setIsFontProcessing(false);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveFont = async (id: string) => {
    try {
      const res = await BrandApi.RemoveFont(id, "brand");
      if (res.success) {
        toast.success("Font removed");
        await fetchCustomFonts(brandId);
      } else {
        toast.error(res.error || "Failed");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const normalizeColor = (color: any) => {
    if (!color) return null;

    if (
      color?.rgb?.r != null &&
      color?.rgb?.g != null &&
      color?.rgb?.b != null
    ) {
      return color;
    }

    if (!color?.hex) return color;

    const clean = color.hex.replace("#", "");

    const rgb = {
      r: parseInt(clean.substring(0, 2), 16),
      g: parseInt(clean.substring(2, 4), 16),
      b: parseInt(clean.substring(4, 6), 16),
    };

    return {
      hex: color.hex,
      rgb,
    };
  };

  const formik = useFormik({
    initialValues: {
      brand_name: "",
      brand_country: "",
      theme_colors: [],
      theme_colors_response: [],
      brand_logo: null,
      brandLogoUploadRecord: null,
      brandFonts: [],
      font_configuration: "brand_fonts",
      title_fonts: "design_default",
      subtitle_fonts: "design_default",
      body_text_fonts: "design_default",
      title_fonts_selected_from: null as any,
      subtitle_fonts_selected_from: null as any,
      body_text_fonts_selected_from: null as any,
      base_color_primary: null,
      base_color_secondary: null,
      text_color_primary: null,
      text_color_secondary: null,
    },
    validationSchema: Yup.object({
      brand_name: Yup.string().required("Brand name is required"),
      brand_country: Yup.string().required("Country is required"),
    }),
    validateOnMount: true,
    onSubmit: async (values) => {
      if (!LocalStorageHelpers.isSuperAdmin()) {
        toast.error("Permission denied. Only Super Admin can edit.");
        return;
      }
      if (!brandId) return;

      setIsSaving(true);

      const formData = new FormData();
      formData.append("brand_name", values.brand_name);
      formData.append("brand_country", values.brand_country);
      formData.append("font_configuration", values.font_configuration);
      formData.append("title_fonts", values.title_fonts);
      formData.append("subtitle_fonts", values.subtitle_fonts);
      formData.append("body_text_fonts", values.body_text_fonts);
      formData.append(
        "title_fonts_selected_from",
        values.title_fonts === "design_default"
          ? ""
          : values.title_fonts_selected_from || "",
      );
      formData.append(
        "subtitle_fonts_selected_from",
        values.subtitle_fonts === "design_default"
          ? ""
          : values.subtitle_fonts_selected_from || "",
      );
      formData.append(
        "body_text_fonts_selected_from",
        values.body_text_fonts === "design_default"
          ? ""
          : values.body_text_fonts_selected_from || "",
      );

      const cleanColors = values.theme_colors?.slice(0, 4) || [];
      formData.append("theme_colors_response", JSON.stringify(cleanColors));

      if (values.base_color_primary) {
        formData.append(
          "base_color_primary",
          JSON.stringify(values.base_color_primary),
        );
      }
      if (values.base_color_secondary) {
        formData.append(
          "base_color_secondary",
          JSON.stringify(values.base_color_secondary),
        );
      }
      if (values.text_color_primary) {
        formData.append(
          "text_color_primary",
          JSON.stringify(values.text_color_primary),
        );
      }
      if (values.text_color_secondary) {
        formData.append(
          "text_color_secondary",
          JSON.stringify(values.text_color_secondary),
        );
      }

      try {
        const response = await BrandApi.updateBrandDetails(brandId, formData);
        if (response.success) {
          toast.success("Brand updated successfully");
          window.location.reload();
        } else {
          toast.error(response.error || "Failed to update brand");
        }
      } catch (err) {
        toast.error("An error occurred");
      } finally {
        setIsSaving(false);
      }
    },
  });

  useEffect(() => {
    const values = formik.values;
    const warnings: Record<string, string> = {};

    const rgbToHex = (r: number, g: number, b: number) => {
      return (
        "#" +
        [r, g, b]
          .map((x) => {
            const hex = Number(x).toString(16);
            return hex.length === 1 ? "0" + hex : hex;
          })
          .join("")
      );
    };

    const getLuminance = (r: number, g: number, b: number) => {
      const a = [r, g, b].map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });

      return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
    };

    const getContrastRatio = (color1: string, color2: string) => {
      const hexToRgbObject = (hex: string) => {
        const cleaned = hex.replace("#", "");
        return {
          r: parseInt(cleaned.substring(0, 2), 16),
          g: parseInt(cleaned.substring(2, 4), 16),
          b: parseInt(cleaned.substring(4, 6), 16),
        };
      };

      const rgb1 = hexToRgbObject(color1);
      const rgb2 = hexToRgbObject(color2);

      const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
      const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);

      return (brightest + 0.05) / (darkest + 0.05);
    };

    const safeHex = (color: any) => {
      if (!color) return null;

      if (color.rgb?.r != null) {
        return rgbToHex(color.rgb.r, color.rgb.g, color.rgb.b);
      }

      if (color.r != null) {
        return rgbToHex(color.r, color.g, color.b);
      }

      if (typeof color.hex === "string") {
        return color.hex;
      }

      return null;
    };

    const basePrimary = safeHex(values.base_color_primary);
    const textPrimary = safeHex(values.text_color_primary);

    if (basePrimary && textPrimary) {
      const contrast = getContrastRatio(basePrimary, textPrimary);
      if (contrast < 4.5) {
        warnings.primaryContrast =
          "Primary text color is difficult to read on Base Primary color.";
      }
    }

    const baseSecondary = safeHex(values.base_color_secondary);
    const textSecondary = safeHex(values.text_color_secondary);

    if (baseSecondary && textSecondary) {
      const contrast = getContrastRatio(baseSecondary, textSecondary);
      if (contrast < 4.5) {
        warnings.secondaryContrast =
          "Secondary text color is difficult to read on Base Secondary color.";
      }
    }

    setContrastWarnings(warnings);
  }, [
    formik.values.base_color_primary,
    formik.values.base_color_secondary,
    formik.values.text_color_primary,
    formik.values.text_color_secondary,
  ]);

  const handleBrandAction = async (id: string, action: string) => {
    if (!id) return;

    if (!canEdit) {
      toast.error("Permission denied.");
      return;
    }

    let statusToSend = "";

    switch (action) {
      case "lock":
        statusToSend = CommonEnums.status.brand_locked;
        break;
      case "unlock":
        statusToSend = CommonEnums.status.active;
        break;
      case "approve":
        statusToSend = CommonEnums.status.active;
        break;
      case "activate":
        statusToSend = CommonEnums.status.active;
        break;
      case "deactivate":
        statusToSend = CommonEnums.status.deactivated;
        break;
      case "archive":
        statusToSend = CommonEnums.status.brand_archive;
        break;
      default:
        return;
    }

    try {
      const formData = new FormData();
      formData.append("status", statusToSend);

      const response = await BrandApi.updateBrandDetails(id, formData);

      if (response.success) {
        toast.success("Brand status updated successfully");
        fetchBrandDetails(id);
      } else {
        toast.error(response.error || "Failed to update status");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  const handleAgencyApprove = async () => {
    if (!canEdit) {
      toast.error("Permission denied.");
      return;
    }

    if (!brandId || !associatedAgencyId) {
      toast.error("Missing Brand or Agency information");
      return;
    }

    setIsApproving(true);

    try {
      const formData = new FormData();
      formData.append("status", "active");

      const response = await BrandApi.updateBrandDetails(brandId, formData);

      if (response.success) {
        toast.success("Agency draft approved.");
        navigate(`/agencies/view/${associatedAgencyId}?type=info`);
      } else {
        toast.error(response.error || "Failed to approve draft");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setIsApproving(false);
    }
  };

  const getFontById = (id: string) => {
    if (!id || id === "design_default") return null;
    return (
      customFonts.find((f) => f.value === id) ||
      systemFonts.find((f) => f.value === id) ||
      null
    );
  };
  const isPreviewReady = !!formik.values?.brand_logo;

  const COUNTRY_OPTIONS = [
    { value: "australia", label: "Australia" },
    { value: "new_zealand", label: "New Zealand" },
  ];

  const handleSystemFontSelected = (font: FontItem) => {
    setSystemFonts((prev) => {
      const alreadyExists = prev.some((f) => f.value === font.value);
      if (alreadyExists) return prev;
      return [...prev, font];
    });
  };

  const getStatusConfig = (status?: string) => {
    const normalized = status?.toLowerCase() || "";

    const label = normalized
      .replace("_", " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

    let className = "inactive";

    if (normalized === "active") {
      className = "active";
    } else if (normalized === "agency_draft") {
      className = "agency-draft";
    }

    return {
      label,
      className,
    };
  };

  if (isLoading) {
    return (
      <NavigationLayout>
        <div className="flex justify-center items-center h-screen">
          <Loading />
        </div>
      </NavigationLayout>
    );
  }

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="edit-brand-wrapper create-brand-wrapper">
          <div
            className="propertie-view-back-btn"
            onClick={() => navigate("/brand-list")}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to Brands list</p>
          </div>

          <div className="metadata-header">
            <div className="metadata-content">
              <div className="header-row">
                <h2 className="brand-title">Brand Info</h2>
                <div className="action-icon-btn">
                  <ActionMenuIcon
                    brandId={brandId!}
                    status={brandMeta.status}
                    onAction={handleBrandAction}
                    canEdit={canEdit}
                  />
                </div>
              </div>

              {brandMeta.status === "agency_draft" && (
                <div className="agency-draft-banner">
                  <div className="agency-draft-content">
                    <div>
                      <p className="agency-draft-text">Agency draft</p>
                      <p className="agency-note">
                        This brand was created by the agency. To verify the
                        agency’s registration request, please review and approve
                        the brand information below.
                      </p>
                    </div>

                    <div className="agency-draft-actions">
                      <button
                        className="btn-approve"
                        onClick={handleAgencyApprove}
                        disabled={isApproving}
                        style={{
                          opacity: isApproving ? 0.6 : 1,
                          cursor: isApproving ? "not-allowed" : "pointer",
                        }}
                      >
                        {isApproving ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          "Approve & continue agency verification"
                        )}
                      </button>

                      <button
                        className="btn-view-info"
                        onClick={() => {
                          if (associatedAgencyId) {
                            navigate(
                              `/agencies/view/${associatedAgencyId}?type=info`,
                            );
                          } else {
                            toast.error("Agency details not found");
                          }
                        }}
                      >
                        View agency info
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="metadata-grid">
                <div className="meta-item">
                  <label>Brand Id</label>
                  <span className="meta-value">{brandId}</span>
                </div>
                <div className="meta-item">
                  <label>Status</label>
                  <div>
                    {(() => {
                      const status = getStatusConfig(brandMeta.status);

                      return (
                        <span className={`status-badge ${status.className}`}>
                          {status.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="meta-item">
                  <label>Created On</label>
                  <span className="meta-value">{brandMeta.createdAt}</span>
                </div>
                <div className="meta-item">
                  <label>Last Updated On</label>
                  <span className="meta-value">{brandMeta.updatedAt}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grammar-section brand-basic-info">
            <div className="brand-basic-grid">
              <div className="input-wrapper">
                <label>Brand Name*</label>
                <input
                  type="text"
                  placeholder="Enter brand name"
                  value={formik.values.brand_name}
                  onChange={formik.handleChange}
                  name="brand_name"
                  className="brand-input"
                />
              </div>

              <div className="input-wrapper">
                <label>Country*</label>

                <FormControl fullWidth>
                  <Select
                    value={formik.values.brand_country}
                    onChange={(e) =>
                      formik.setFieldValue("brand_country", e.target.value)
                    }
                    displayEmpty
                    className="brand-country-select"
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span className="placeholder">Select country</span>
                        );
                      }

                      const option = COUNTRY_OPTIONS.find(
                        (item) => item.value === selected,
                      );

                      return option ? option.label : selected;
                    }}
                  >
                    {COUNTRY_OPTIONS.map((country) => (
                      <MenuItem key={country.value} value={country.value}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          <div className="create-brand-container">
            <div className="create-brand-left">
              <div className="grammar-section">
                <div className="section-title">Brand/Franchise Logo*</div>
                <div className="brand-section-desc brand-desc">
                  Upload the Brand’s official logo using the specifications
                  below.
                  <ul>
                    <li>
                      <span className="office-logo">Dimensions:</span>
                      Recommended size is 510 x 96 pixels.
                    </li>
                    <li>
                      <span className="office-logo">Background:</span> Must be a
                      solid background (no transparency).
                    </li>
                    <li>
                      <span className="office-logo">Quality:</span> Please
                      provide a high-resolution file.
                    </li>
                    <li>Accepted files are PNG, JPG, or JPEG.</li>
                  </ul>
                </div>
                <BrandProfileLogo
                  formik={formik}
                  brandId={brandId}
                  setBrandId={setBrandId}
                  canEdit={canEdit}
                />
              </div>

              <div className="grammar-section">
                <div className="section-title">Brand/Franchisee Colors*</div>
                <div className="section-desc brand-desc">
                  These colors are automatically extracted from the brand logo.
                </div>
                {formik.values.brand_logo ? (
                  <ThemePreferences
                    formik={formik}
                    isLocked={!canEdit}
                    originalColors={originalColors}
                  />
                ) : (
                  <div className="placeholder-box">
                    <p>Upload Brand/Franchisee logo to generate the colors</p>
                  </div>
                )}
              </div>

              <ThemeErrorSummary errors={contrastWarnings} />

              <div className="scrollable-form-body">
                <div className="typography-form-group">
                  <label className="typography-group-label">
                    Upload Brand Fonts
                  </label>
                  <p className="typography-group-desc">
                    Official brand fonts used to generate marketing collateral.
                  </p>

                  <div
                    className={`upload-box ${isFontUploading ? "uploading" : ""}`}
                    onDrop={
                      !isFontUploading && canEdit
                        ? (e) => {
                            e.preventDefault();
                            handleFontFiles(e.dataTransfer.files);
                          }
                        : (e) => e.preventDefault()
                    }
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => {
                      if (!canEdit) {
                        toast.error("Permission denied.");
                        return;
                      }
                      if (!isFontUploading) fileInputRef.current?.click();
                    }}
                    style={{
                      opacity: !canEdit ? 0.6 : 1,
                      cursor: !canEdit ? "not-allowed" : "pointer",
                    }}
                  >
                    <input
                      type="file"
                      hidden
                      multiple
                      ref={fileInputRef}
                      accept=".zip,.rar,.ttf,.otf,.woff2"
                      disabled={isFontUploading || !canEdit}
                      onChange={(e) =>
                        e.target.files && handleFontFiles(e.target.files)
                      }
                    />
                    <div className="icon-wrapper">
                      {isFontUploading ? (
                        <CircularProgress
                          size={28}
                          style={{ color: "black" }}
                        />
                      ) : (
                        <Upload />
                      )}
                    </div>
                    <p className="upload-text">
                      {isFontUploading
                        ? "Uploading & processing..."
                        : "Drag & Drop your font files here or Click to Browse"}
                    </p>
                  </div>
                  <p className="support-text">
                    Supported: .zip, .rar, .ttf, .otf, .woff2 (Multiple
                    selection allowed)
                  </p>

                  {formik.values.brandFonts.length > 0 && (
                    <div className="uploaded-files-list">
                      {formik.values.brandFonts.map(
                        (font: any, idx: number) => (
                          <div key={idx} className="file-chip font-family-chip">
                            <img
                              src="/images/font-icon.png"
                              alt="font"
                              className="font-file-icon"
                            />
                            <div className="font-info">
                              <span
                                className="font-family-name"
                                style={{
                                  fontFamily: font.family || "inherit",
                                  fontStyle: "normal",
                                  fontWeight: 400,
                                }}
                              >
                                {font.family}
                              </span>
                              <span className="available-weights-label">
                                Available weights
                              </span>
                              <span className="available-weights-list">
                                {font.availableWeights?.join(", ") || "-"}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="remove-font-btn"
                              onClick={() =>
                                canEdit && handleRemoveFont(font.id)
                              }
                              disabled={!canEdit}
                              style={{
                                cursor: !canEdit ? "not-allowed" : "pointer",
                                opacity: !canEdit ? 0.5 : 1,
                              }}
                              title={`Remove ${font.family} family and all its variants`}
                            >
                              ✕
                            </button>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="typography-form-group">
                <label className="typography-group-label">Typography</label>
                <p className="typography-group-desc">
                  Configure fonts to match the brand's design specifications.
                </p>

                <div className="typography-grid">
                  <div className="select-wrapper">
                    <label>Title</label>
                    <div
                      className="custom-select-box"
                      onClick={() => setOpenTitleFontModal(true)}
                    >
                      <div
                        className="select-render"
                        style={{
                          fontFamily:
                            getFontById(formik.values.title_fonts)?.family ||
                            "inherit",
                        }}
                      >
                        <SearchIcon />
                        {getFontById(formik.values.title_fonts)?.label ||
                          "Design default"}
                      </div>
                    </div>

                    <FontSelectorModal
                      open={openTitleFontModal}
                      onClose={() => setOpenTitleFontModal(false)}
                      value={formik.values.title_fonts}
                      onChange={(val, font, source) => {
                        formik.setFieldValue("title_fonts", val);
                        formik.setFieldValue(
                          "title_fonts_selected_from",
                          source || null,
                        );
                      }}
                      customFonts={customFonts}
                      // systemFonts={systemFonts}
                      customFontSectionLabel={customFontSectionLabel}
                      brandId={brandId || undefined}
                      fontsFor="brand"
                      onSystemFontSelected={handleSystemFontSelected}
                    />
                  </div>

                  <div className="select-wrapper">
                    <label>Subtitle</label>
                    <div
                      className="custom-select-box"
                      onClick={() => setOpenSubtitleFontModal(true)}
                    >
                      <div
                        className="select-render"
                        style={{
                          fontFamily:
                            getFontById(formik.values.subtitle_fonts)?.family ||
                            "inherit",
                        }}
                      >
                        <SearchIcon />
                        {getFontById(formik.values.subtitle_fonts)?.label ||
                          "Design default"}
                      </div>
                    </div>

                    <FontSelectorModal
                      open={openSubtitleFontModal}
                      onClose={() => setOpenSubtitleFontModal(false)}
                      value={formik.values.subtitle_fonts}
                      onChange={(val, font, source) => {
                        formik.setFieldValue("subtitle_fonts", val);
                        formik.setFieldValue(
                          "subtitle_fonts_selected_from",
                          source || null,
                        );
                      }}
                      customFonts={customFonts}
                      // systemFonts={systemFonts}
                      customFontSectionLabel={customFontSectionLabel}
                      brandId={brandId || undefined}
                      fontsFor="brand"
                      onSystemFontSelected={handleSystemFontSelected}
                    />
                  </div>
                </div>

                <div className="select-wrapper full-width-select">
                  <label>Body text</label>
                  <div
                    className="custom-select-box"
                    onClick={() => setOpenBodyFontModal(true)}
                  >
                    <div
                      className="select-render"
                      style={{
                        fontFamily:
                          getFontById(formik.values.body_text_fonts)?.family ||
                          "inherit",
                      }}
                    >
                      <SearchIcon />
                      {getFontById(formik.values.body_text_fonts)?.label ||
                        "Design default"}
                    </div>
                  </div>

                  <FontSelectorModal
                    open={openBodyFontModal}
                    onClose={() => setOpenBodyFontModal(false)}
                    value={formik.values.body_text_fonts}
                    onChange={(val, font, source) => {
                      formik.setFieldValue("body_text_fonts", val);
                      formik.setFieldValue(
                        "body_text_fonts_selected_from",
                        source || null,
                      );
                    }}
                    customFonts={customFonts}
                    // systemFonts={systemFonts}
                    customFontSectionLabel={customFontSectionLabel}
                    brandId={brandId || undefined}
                    fontsFor="brand"
                    onSystemFontSelected={handleSystemFontSelected}
                  />
                </div>
              </div>

              <div className="button-group">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => navigate("/brand-list")}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => formik.handleSubmit()}
                  disabled={!formik.isValid || !canEdit || isSaving}
                  style={{
                    opacity: !canEdit || isSaving ? 0.6 : 1,
                    cursor: !canEdit || isSaving ? "not-allowed" : "pointer",
                  }}
                  title={!canEdit ? "Only Super Admin can edit details" : ""}
                >
                  {isSaving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Save changes"
                  )}
                </button>
              </div>
            </div>

            <div className="right-column">
              <div className="grammar-section">
                <div className="section-title">
                  Preview Post with Brand Info
                </div>
                <div className="section-desc right-grammer-desc">
                  A live look for the sample post with brand logo, colors and
                  fonts
                </div>
                <div className="preview-wrapper">
                  <div className="preview-card">
                    {isPreviewReady ? (
                      <img
                        src="/images/brand_grammer_sample.png"
                        alt="Preview"
                        className="live-preview-img"
                      />
                    ) : (
                      <div className="empty-preview-content">
                        <img
                          src="/icons/pending-icon-2.png"
                          alt="Pending"
                          className="waiting-icon"
                        />
                        <p>Upload brand logo to generate preview</p>
                      </div>
                    )}
                  </div>
                  {isPreviewReady && (
                    <div className="preview-actions">
                      <button
                        type="button"
                        onClick={() => setShowMobilePreview(true)}
                      >
                        Preview
                      </button>
                      <button type="button">Download</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className={`mobile-bottom-drawer ${isDrawerOpen ? "open" : ""}`}
            >
              <div
                className="drawer-header"
                onClick={() => setIsDrawerOpen(true)}
              >
                <div className="drawer-handle-line"></div>
                <span className="drawer-title">Preview post</span>
              </div>

              <div className="drawer-content">
                <div className="drawer-scroll-area">
                  <div className="mobile-preview-card">
                    <div className="preview-text-info">
                      <h3>Preview Post with Brand Info</h3>
                      <p>
                        A live look at your customized marketing content, styled
                        with your agency’s unique brand identity
                      </p>
                    </div>

                    {isPreviewReady ? (
                      <img
                        src="/images/brand_grammer_sample.png"
                        alt="Post Preview"
                        className="live-preview-img"
                      />
                    ) : (
                      <div className="empty-preview-content">
                        <img
                          src="/icons/pending-icon-2.png"
                          alt="Waiting Icon"
                          className="waiting-icon"
                        />
                        <p>
                          Upload brand logo and brand fonts to generate the
                          preview
                        </p>
                      </div>
                    )}

                    {isPreviewReady && (
                      <div className="drawer-actions">
                        <button type="button" className="btn-preview">
                          Preview
                        </button>
                        <button type="button" className="btn-download">
                          Download
                        </button>
                      </div>
                    )}

                    <button
                      className="close-drawer-btn"
                      onClick={() => setIsDrawerOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal
          open={isFontUploading || isFontProcessing}
          onClose={() => {}}
          disableEscapeKeyDown
          hideBackdrop={false}
        >
          <Box className="font-uploading-modal">
            <div className="font-uploading-spinner"></div>

            <div className="font-uploading-texts">
              <p className="font-uploading-title">Uploading fonts...</p>
              <p className="font-uploading-subtitle">
                Please wait while we process your fonts.
              </p>
            </div>
          </Box>
        </Modal>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default EditBrand;
